/* PrintTargetDialog — "Yazdır" tıklandığında yazıcı seçim modal'ı.
 *
 * Akış:
 *   1. PDF blob'u (zaten backend'den alındı) prop olarak gelir
 *   2. PrintNode configured ise yazıcı listesi gösterilir + "Browser Print" opsiyonu
 *   3. Yazıcı seçilince capabilities çekilir → paper / fit_to_page / rotate seçimi
 *   4. Kullanıcı "Yazıcıya Gönder" → submit → toast
 *
 *  Lokal yazdırmada bozulma yok; sadece PrintNode'a giderken etiket küçük/dik çıkıyor.
 *  Çözüm: PrintNode `options` payload'u (fit_to_page + paper + rotate) ile düzeltiyoruz.
 *  PDF dosyasının kendisi hiç değişmiyor.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { X, Printer, Loader2, Monitor, CheckCircle2, AlertCircle, Settings2 } from 'lucide-react';
import {
    usePrintNodeConfig,
    usePrintNodePrinters,
    usePrintNodePrinterCapabilities,
    usePrintNodeSubmit,
} from '../../../hooks/usePrintNode';

/** Termal etiket için en uygun papers tahmini (~A6 ≈ 100x150 mm). */
function pickBestLabelPaper(papers) {
    if (!papers || !papers.length) return '';
    const score = (p) => {
        const w = p.width_mm || 0;
        const h = p.height_mm || 0;
        const long = Math.max(w, h);
        const short = Math.min(w, h);
        // Hedef: ~100x150 (Bol kargo etiketi ≈ A6)
        return Math.abs(long - 150) + Math.abs(short - 100);
    };
    return [...papers].sort((a, b) => score(a) - score(b))[0]?.name || papers[0].name;
}

export default function PrintTargetDialog({ pdfBlob, title, copies = 1, onClose }) {
    const [selectedPrinterId, setSelectedPrinterId] = useState(null);
    const [copyCount, setCopyCount] = useState(copies);
    const [paper, setPaper] = useState('');
    const [fitToPage, setFitToPage] = useState(true);
    const [rotate, setRotate] = useState(0);
    const [autoRotate, setAutoRotate] = useState(true);
    const [useDriverDefaults, setUseDriverDefaults] = useState(true);  // Iter 113.22: default ON
    const [showAdvanced, setShowAdvanced] = useState(false);

    const configQuery = usePrintNodeConfig();
    const configured = configQuery.data?.configured;
    const printersQuery = usePrintNodePrinters({ enabled: !!configured });
    const capsQuery = usePrintNodePrinterCapabilities(selectedPrinterId, { enabled: !!selectedPrinterId });
    const submitMut = usePrintNodeSubmit();

    // Default printer ön-seçimi
    useEffect(() => {
        if (printersQuery.data?.default_printer_id && !selectedPrinterId) {
            setSelectedPrinterId(printersQuery.data.default_printer_id);
        }
    }, [printersQuery.data, selectedPrinterId]);

    // Capabilities geldiğinde paper ön-seçimi (önce kullanıcı tercihi, sonra A6 tahmini)
    const papers = capsQuery.data?.papers || [];
    const saved = capsQuery.data?.saved_prefs || {};
    useEffect(() => {
        if (!capsQuery.data) return;
        if (saved.paper && papers.find((p) => p.name === saved.paper)) {
            setPaper(saved.paper);
        } else {
            setPaper(pickBestLabelPaper(papers));
        }
        if (typeof saved.fit_to_page === 'boolean') setFitToPage(saved.fit_to_page);
        else setFitToPage(true);
        if (typeof saved.auto_rotate === 'boolean') setAutoRotate(saved.auto_rotate);
        else setAutoRotate(true);
        if (typeof saved.use_driver_defaults === 'boolean') setUseDriverDefaults(saved.use_driver_defaults);
        else setUseDriverDefaults(true);
        setRotate([0, 90, 180, 270].includes(saved.rotate) ? saved.rotate : 0);
    }, [capsQuery.data, papers, saved]);

    const selectedPaperInfo = useMemo(
        () => papers.find((p) => p.name === paper),
        [papers, paper],
    );

    if (!pdfBlob) return null;

    const printers = printersQuery.data?.printers || [];

    const handleBrowserPrint = () => {
        const url = URL.createObjectURL(pdfBlob);
        const w = window.open(url, '_blank');
        if (w) {
            w.addEventListener('load', () => { try { w.print(); } catch { /* ignore */ } });
        }
        toast.success('Tarayıcı yazdırma penceresi açıldı');
        onClose?.();
    };

    const handleRemotePrint = async () => {
        if (!selectedPrinterId) {
            toast.error('Yazıcı seç');
            return;
        }
        try {
            const r = await submitMut.mutateAsync({
                pdfBlob,
                printerId: selectedPrinterId,
                title: title || 'Flowbil Print',
                copies: copyCount,
                paper,
                fitToPage,
                rotate,
                autoRotate,
                useDriverDefaults,
            });
            const printerName = printers.find((p) => p.id === selectedPrinterId)?.name || `#${selectedPrinterId}`;
            const rotatedN = r.rotated_pages || 0;
            const autoMsg = rotatedN > 0 ? ` (${rotatedN} sayfa otomatik döndürüldü)` : '';
            toast.success(`${printerName} → Job #${r.job_id}${autoMsg}`, { duration: 5000 });
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'PrintNode hatası');
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="print-target-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Printer size={18} className="text-cyan-700" />
                        <h3 className="text-base font-bold text-gray-900">Yazıcı Seç</h3>
                    </div>
                    <button onClick={onClose}
                        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                        data-testid="print-dialog-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 space-y-2">
                    {configQuery.isLoading ? (
                        <div className="text-center py-6 text-gray-500">
                            <Loader2 className="animate-spin mx-auto" size={20} />
                        </div>
                    ) : !configured ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                            <AlertCircle size={14} className="inline mr-1 -mt-0.5" />
                            <b>PrintNode yapılandırılmamış.</b> Uzaktan yazdırma için:{' '}
                            <a href="/integrations?integration=printnode" target="_blank" rel="noreferrer"
                                className="underline font-semibold text-cyan-700 hover:text-cyan-900">
                                Entegrasyonlar → PrintNode
                            </a>{' '}
                            sayfasından API key ekle. Şimdilik tarayıcı yazdırması ile devam edebilirsin.
                        </div>
                    ) : printersQuery.isLoading ? (
                        <div className="text-center py-6 text-gray-500">
                            <Loader2 className="animate-spin mx-auto" size={20} />
                            <div className="text-xs mt-2">PrintNode yazıcıları getiriliyor...</div>
                        </div>
                    ) : printers.length === 0 ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-900">
                            <b>Yazıcı bulunamadı.</b> Fabrikadaki PC&apos;de PrintNode Client&apos;ın açık olduğundan emin ol.
                        </div>
                    ) : (
                        <>
                            <div className="text-[11px] text-gray-500 font-semibold uppercase mb-1">Uzaktan Yazıcı</div>
                            {printers.map((p) => (
                                <label key={p.id}
                                    className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${
                                        selectedPrinterId === p.id ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <input type="radio"
                                            checked={selectedPrinterId === p.id}
                                            onChange={() => setSelectedPrinterId(p.id)}
                                            data-testid={`printer-${p.id}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-gray-900 truncate">{p.name}</div>
                                            <div className="text-[10px] text-gray-500 truncate">
                                                {p.computer_name && `${p.computer_name} · `}
                                                <span className={p.state === 'online' ? 'text-emerald-700 font-semibold' : 'text-rose-700'}>
                                                    {p.state}
                                                </span>
                                                {p.is_default_in_flowbil && ' · varsayılan'}
                                            </div>
                                        </div>
                                    </div>
                                    {p.state === 'online' ? (
                                        <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                                    ) : (
                                        <AlertCircle size={14} className="text-amber-500 shrink-0" />
                                    )}
                                </label>
                            ))}

                            {/* Paper / Options panel */}
                            {selectedPrinterId && (
                                <div className="mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                                    {capsQuery.isLoading ? (
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                            <Loader2 size={12} className="animate-spin" />
                                            Yazıcı kabiliyetleri yükleniyor...
                                        </div>
                                    ) : (
                                        <>
                                            {/* Iter 113.47: use_driver_defaults kaldırıldı — backend content bbox + native placement,
                                                 fit_to_page HARD invariant false. Sadece paper name + auto_rotate kaldı. */}
                                            <div>
                                            <div className="flex items-center justify-between">
                                                <label className="text-[11px] font-semibold text-gray-700 uppercase">
                                                    Kağıt Boyutu
                                                </label>
                                                {selectedPaperInfo?.width_mm && (
                                                    <span className="text-[10px] text-gray-500">
                                                        {selectedPaperInfo.width_mm} × {selectedPaperInfo.height_mm} mm
                                                    </span>
                                                )}
                                            </div>
                                            <select value={paper}
                                                onChange={(e) => setPaper(e.target.value)}
                                                className="w-full h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                                                data-testid="print-paper-select">
                                                {papers.length === 0 && <option value="">(otomatik)</option>}
                                                {papers.map((p) => (
                                                    <option key={p.name} value={p.name}>
                                                        {p.name}
                                                        {p.width_mm ? `  —  ${p.width_mm}×${p.height_mm} mm` : ''}
                                                    </option>
                                                ))}
                                            </select>

                                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer pt-2">
                                                <input type="checkbox"
                                                    checked={autoRotate}
                                                    onChange={(e) => setAutoRotate(e.target.checked)}
                                                    data-testid="print-auto-rotate-checkbox" />
                                                <span><b>Akıllı sığdır</b> — dikey/yatay otomatik döndür (önerilen: açık)</span>
                                            </label>

                                            <button
                                                onClick={() => setShowAdvanced((v) => !v)}
                                                type="button"
                                                className="flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-900 pt-1"
                                                data-testid="print-advanced-toggle">
                                                <Settings2 size={11} />
                                                {showAdvanced ? 'Gelişmişi gizle' : 'Gelişmiş ayarlar'}
                                            </button>

                                            {showAdvanced && (
                                                <div className="pt-1">
                                                    <div className="text-[11px] font-semibold text-gray-700 uppercase mb-1">Döndürme</div>
                                                    <div className="flex gap-1">
                                                        {[0, 90, 180, 270].map((deg) => (
                                                            <button key={deg}
                                                                type="button"
                                                                onClick={() => setRotate(deg)}
                                                                className={`flex-1 h-7 text-[11px] rounded border ${
                                                                    rotate === deg
                                                                        ? 'bg-cyan-700 text-white border-cyan-700'
                                                                        : 'bg-white border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                                data-testid={`print-rotate-${deg}`}>
                                                                {deg}°
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Browser print fallback */}
                    <div className="pt-2 mt-2 border-t border-gray-200">
                        <button onClick={handleBrowserPrint}
                            className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                            data-testid="browser-print-btn">
                            <Monitor size={14} className="text-gray-600" />
                            <div className="text-left flex-1">
                                <div className="font-medium text-gray-900">Tarayıcı Yazdırma</div>
                                <div className="text-[10px] text-gray-500">Bu cihazdan, bağlı yazıcıyla yazdır</div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Footer: copies + submit */}
                {configured && printers.length > 0 && (
                    <div className="p-3 border-t border-gray-200 flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                            <span>Kopya:</span>
                            <input type="number" min={1} max={20} value={copyCount}
                                onChange={(e) => setCopyCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                                className="h-7 w-14 text-center text-xs border border-gray-300 rounded"
                                data-testid="print-copies-input" />
                        </label>
                        <button onClick={handleRemotePrint}
                            disabled={!selectedPrinterId || submitMut.isPending}
                            className="ml-auto h-9 px-4 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-lg flex items-center gap-1.5"
                            data-testid="remote-print-btn">
                            {submitMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                            {submitMut.isPending ? 'Gönderiliyor...' : 'Yazıcıya Gönder'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
